import React, { useState, useEffect } from "react";

import { uploadDictation, uploadDictatioProvider } from "apiHandler/actions";
import { displayDateFormatter } from "components/helpers";

import useRecorder from "components/customHooks/useRecorderMp3";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";

import { useUserData } from "components/customHooks/userDataContext";

import MicIcon from "@material-ui/icons/Mic";

import dayjs from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { getFormattedDate } from "utils/helper";
import { PauseCircleOutline, StopCircleOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setMicrophoneVerified } from "../../redux/reducers/dictation";

const UploadDictation = (props) => {
  const userData = useUserData();
  const practice_template = userData.practice_template;

  const [practiceId, setPracticeId] = useState(userData?.selected_practice);
  const [npi, setNpi] = useState(userData?.providers?.[0].npi.toString() || "");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const getDefaultTemplate = (templates, selectedNpi) => {
    if (!templates?.length) return "";

    const npiTemplate = templates.find(
      (t) => t?.npi && t.npi.toString() === selectedNpi?.toString(),
    );

    if (npiTemplate) {
      return npiTemplate.template_name;
    }

    const practiceDefault = templates.find(
      (t) => !t?.npi && t?.template_default_ind?.toLowerCase() === "yes",
    );

    if (practiceDefault) {
      return practiceDefault.template_name;
    }

    return "";
  };

  const [template, setTemplate] = useState(
    getDefaultTemplate(practice_template, npi),
  );

  const [dos, setDOS] = useState(
    displayDateFormatter(props?.selectedAppt?.dos) ||
      dayjs().format("MM/DD/YYYY"),
  );
  const [file, setFile] = useState(null);
  const [type, setType] = useState("record");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState("");
  const [count, setCount] = useState(null);
  const [title, setTitle] = useState("");
  const [isLoading, setLoader] = useState(false);
  const [intervalId, setIntervalId] = useState();
  const [micCheckMessage, setMicCheckMessage] = useState("");
  const microphoneVerifiedAt = useSelector(
    (state) => state.dictation.microphoneVerifiedAt,
  );

  const dispatch = useDispatch();

  const encounter_id = props?.selectedAppt?.id || "";

  const [
    audioURL,
    isRecording,
    startRecording,
    stopRecording,
    audioFile,
    audioError,
    togglePause,
    isPaused,
    testMicrophone,
    isSilentRecording,
    recorder,
  ] = useRecorder();

  const token = userData?.access_token;
  const userRole = userData.user_details.user_role;

  const [time, setTime] = useState({
    sec: 0,
    min: 0,
    hr: 0,
  });

  useEffect(() => {
    const defaultTemplate = getDefaultTemplate(practice_template, npi);
    console.log("npi: ", npi);

    setTemplate(defaultTemplate);
  }, [npi, practice_template]);

  const upload = async (e) => {
    e.preventDefault();
    setLoader(true);
    const payload = {
      type: "Dictation",
      practice_id: practiceId,
      npi,
      dos: displayDateFormatter(dos),
      template_name: template,
      title: title,
      provider_name: getProviderName(selectedProvider),
    };

    if (encounter_id != "") payload["encounter_id"] = encounter_id;
    const formData = new FormData();
    formData.append("request_json", JSON.stringify(payload));
    if (type === "choose" && file) formData.append("file", file);
    else if (type === "record" && audioFile) formData.append("file", audioFile);
    else {
      setError("Audio unavailable");
      return;
    }
    if (type === "record") {
      const silent = await isSilentRecording(recorder.recordingBlob);

      if (silent) {
        dispatch(setMicrophoneVerified(null));
        setError(
          "No audio was detected in the recording. Please record again.",
        );
        setLoader(false);
        return;
      }
    }
    const response =
      userRole === "Provider"
        ? await uploadDictatioProvider(formData, token, userRole)
        : await uploadDictation(formData, token, userRole);
    console.log("response: ", response, userRole);

    if (userData?.userType === "scribe" && props.fetchData) {
      props.fetchData();
    }
    if (props.fetchDictations) {
      props.fetchDictations();
    }
    if (userRole === "Provider") {
      props.setSnackbarMessage(
        response.status === "Success"
          ? "Successfully added dictation!"
          : "Error while adding dictation. Please try again!",
      );
    } else {
      props.setSnackbarMessage(response);
    }

    setLoader(false);
    await props.onClose();
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return null;
    const ext = f.name.split(".")[1];
    const allowedExt = ["wav", "mp3", "m4a", "mp4"];
    if (allowedExt.includes(ext)) {
      setFile(f);
    }
  };

  useEffect(() => {
    if (isRecording && !isPaused) {
      const id = setInterval(() => {
        setTime((prev) => {
          let newTime = { ...prev };
          if (newTime.sec < 59) newTime.sec += 1;
          else {
            newTime.min += 1;
            newTime.sec = 0;
          }
          if (newTime.min === 60) {
            newTime.min = 0;
            newTime.hr += 1;
          }
          return newTime;
        });
      }, 1000);
      setIntervalId(id);
    } else if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (count === 0) {
      clearTimeout(countdown);
      startRecording();
      setCount(null);
    }
  }, [count]);

  const initiateRecording = async () => {
    setError("");

    if (!microphoneVerifiedAt) {
      setMicCheckMessage(
        "Microphone check, please say something before continuing",
      );

      const hasAudio = await testMicrophone();

      setMicCheckMessage("");

      if (!hasAudio) {
        setError(
          "No microphone input detected. Please check your microphone and try again.",
        );
        return;
      }

      dispatch(setMicrophoneVerified(Date.now()));
    }
    setCount(3);
    clearInterval(intervalId);
    setTime({
      sec: 0,
      min: 0,
      hr: 0,
    });
    setCountdown(
      setInterval(() => {
        setCount((prev) => {
          if (prev > 0) return prev - 1;
        });
      }, 1000),
    );
    setMicCheckMessage("");
  };

  const stopAudioRecording = async () => {
    stopRecording();
  };

  const handleCancel = (e) => {
    e.preventDefault();

    if (!isRecording) {
      setShowConfirmModal(true);
    }
  };

  const confirmCancel = () => {
    setShowConfirmModal(false);
    props.onClose();
  };

  const closeConfirm = () => {
    setShowConfirmModal(false);
  };

  const selectedProvider =
    userData?.providers?.find((p) => p.npi?.toString() === npi?.toString()) ||
    null;

  const getProviderName = (p) => {
    if (!p) return "";

    return `${p.provider_first_name || ""} ${
      p.provider_last_name || ""
    }`.trim();
  };
  return (
    <>
      <Modal onClose={() => {}} open={true}>
        <form className="upload-dictation-modal">
          <h3 className="udm-title">Upload Dictation</h3>
          {error ? <p className="udm-error">{error}</p> : null}
          {type === "record" && audioError ? (
            <p className="udm-error">{audioError}</p>
          ) : null}
          {micCheckMessage && (
            <Typography color="primary" align="center" sx={{ mt: 1, mb: 1 }}>
              {micCheckMessage}
            </Typography>
          )}
          {props.selectedAppt?.first_name && (
            <Typography
              align="center"
              fontWeight={500}
            >{`${props.selectedAppt?.first_name} ${props.selectedAppt?.last_name}`}</Typography>
          )}
          {props.selectedAppt?.dob && (
            <Typography align="center" mb={2} fontWeight={500}>
              DOB: {getFormattedDate(props.selectedAppt?.dob)}
            </Typography>
          )}
          <RadioGroup
            name="audio-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ flexDirection: "row" }}
          >
            <FormControlLabel
              value="choose"
              control={<Radio />}
              label="Select from Files"
            />
            <FormControlLabel
              value="record"
              control={<Radio />}
              label="Record Audio"
            />
          </RadioGroup>

          {type === "choose" ? (
            <div className="udm-input-div">
              <p className="udm-label">Attach audio file</p>
              <input
                accept=".wav,.mp3,.m4a,.mp4"
                className="udm-input-file"
                onChange={handleFile}
                type="file"
              />
            </div>
          ) : (
            <div className="udm-audio-div">
              {audioError ? (
                <PlayCircleOutlineIcon fontSize="large" color="disabled" />
              ) : isRecording ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <IconButton
                    onClick={stopAudioRecording}
                    sx={{
                      bgcolor: "secondary.main",
                      color: "white",

                      "&:hover": {
                        bgcolor: "secondary.dark",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <StopCircleOutlined fontSize="medium" />
                  </IconButton>

                  <IconButton
                    onClick={togglePause}
                    sx={{
                      bgcolor: isPaused ? "grey.300" : "secondary.light",
                      color: isPaused ? "grey.600" : "secondary.dark",

                      "&:hover": {
                        bgcolor: isPaused ? "grey.400" : "secondary.main",
                        color: "white",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {isPaused ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <PauseCircleOutline fontSize="medium" />
                    )}
                  </IconButton>
                </Box>
              ) : count && count !== 0 ? (
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "#D91A5F",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {count}
                </Avatar>
              ) : (
                <IconButton
                  onClick={initiateRecording}
                  disabled={!template}
                  sx={{
                    color: !template ? "action.disabled" : "secondary.main",
                    "&:hover": {
                      bgcolor: !template
                        ? "action.disabledBackground"
                        : "secondary.light",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <MicIcon fontSize="medium" />
                </IconButton>
              )}

              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                  color: isRecording ? "secondary.main" : "text.primary",
                }}
              >
                {`${time.min.toString().padStart(2, "0")}:${time.sec
                  .toString()
                  .padStart(2, "0")}`}
              </Typography>

              <audio src={audioURL} controls />
            </div>
          )}
          {!template && (
            <Typography variant="body2" color="info" m={2}>
              Please select a template to start recording
            </Typography>
          )}
          <div className="udm-input-div">
            <p className="udm-label">Title</p>
            <input
              value={title}
              className="udm-input"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="udm-input-div">
            <p className="udm-label">Practice ID</p>
            <input
              value={practiceId}
              className="udm-input"
              disabled
              onChange={(e) => setPracticeId(e.target.value)}
            />
          </div>
          <div className="udm-input-div">
            <p className="udm-label">NPI</p>
            <Select
              native
              variant="standard"
              size="small"
              value={npi}
              onChange={(e) => setNpi(e.target.value)}
              className="input"
            >
              {userData.providers.map((s, index) => (
                <option key={index} value={s.npi}>
                  {getProviderName(s) || s.npi.toString()}
                </option>
              ))}
            </Select>
          </div>
          {selectedProvider && (
            <Typography
              variant="body2"
              textAlign="right"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              NPI: {selectedProvider.npi}
            </Typography>
          )}
          <div className="udm-input-div">
            <p className="udm-label">Date of Service</p>
            <DesktopDatePicker
              placeholder="MM/DD/YYYY"
              format="MM/DD/YYYY"
              value={dayjs(dos)}
              // defaultValue={format(new Date(), "MM/dd/yyyy")}
              onChange={(e) => setDOS(e)}
              slotProps={{ textField: { variant: "standard" } }}
            />
          </div>
          <div className="udm-input-div">
            <p className="udm-label">Template</p>
            <Select
              native
              variant="standard"
              size="small"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="input"
            >
              <option value="">Select Template</option>
              {practice_template.map((s, index) => (
                <option key={index} value={s.template_name}>
                  {s.template_display_name}
                </option>
              ))}
            </Select>
          </div>
          <div className="udm-btn-cont">
            {isLoading ? (
              <CircularProgress color="secondary" size="1rem" />
            ) : (
              <Button
                type="submit"
                onClick={upload}
                disabled={!file && !audioFile}
                variant="contained"
                sx={{ minWidth: 90 }}
              >
                Upload
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isRecording}
              sx={{ marginLeft: 2, minWidth: 90 }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        open={showConfirmModal}
        onClose={closeConfirm}
        aria-labelledby="confirm-cancel-title"
        aria-describedby="confirm-cancel-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: 300,
            textAlign: "center",
          }}
        >
          <Typography
            id="confirm-cancel-title"
            variant="h6"
            component="h2"
            mb={3}
          >
            Are you sure you want to cancel?
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-around" }}>
            <Button
              variant="contained"
              color="error"
              onClick={confirmCancel}
              sx={{ minWidth: 90 }}
            >
              Yes
            </Button>
            <Button
              variant="outlined"
              onClick={closeConfirm}
              sx={{ minWidth: 90 }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UploadDictation;
